import * as React from "react";
import styled, {
  css,
  useTheme
} from "styled-components";
import { lighten } from "polished";
import { useMappedState } from "redux-react-hook";
import { useHistory } from "react-router-dom";

import Grid from "src/library/components/Hedron";
import { Datetime } from "src/library/components/Datetime";
import {
  Text,
  TextTruncate
} from "src/library/components/Text/Text";
import { Icon } from "src/library/components/Icon";
import { breakpoint } from "src/library/utils/styled-media-queries/index";
import { Icons } from "src/library/components/Icon/types";
import { ContextHeight } from "src/context/SideBarContext/utils";
import { calculateCartTotalQuantity } from "src/core/orders/utils/orders";
import { usePriceVisibility } from "src/core/prices/hooks/use-price-visibility";
import { useOrderCutoffTime } from "src/core/orders/hooks/validation";
import useOrder from "src/core/orders/hooks/useOrder";
import { useDrop } from "src/core/drops/hooks";
import { formatDropName } from "src/core/drops/utils/format-drop-name";
import { IStore } from "src/core/shared/store";
import { TotalPrice } from "src/core/prices/components/TotalPrice";

export const InOrderContext: React.ReactFCWithChildren = () => {
  const [ { currentOrder: { data: order, mode } } ] = useOrder();
  const orderCutoffTime = useOrderCutoffTime();
  const pricesVisible = usePriceVisibility();
  const history = useHistory();
  const [ { drop } ] = useDrop();
  const { terminology } = useMappedState(({ global }: IStore) => ({ terminology: global.settings?.terminology }));
  const theme = useTheme();

  const cutoffTime = React.useMemo(() => {
    if (order) {
      return orderCutoffTime(order, "nearest");
    }

    return null;
  }, [ order, orderCutoffTime ]);

  const dropName = React.useMemo(() => drop ? formatDropName(drop, true) : null, [ drop ]);

  if (!order) {
    return null;
  }

  return (
    <InOrderWrapper>
      <div className="context-item drop-name-context-item">
        <Text
          className="context-item-label"
          fontSize="sub"
          color="white"
        >
          {terminology?.drop.singular.text}
        </Text>

        <div className="context-item-value">
          <Icon
            icon={Icons.BRANCH}
            $fillColor="white"
            size={15}
          />

          {dropName && (
            <TextTruncate
              truncateProps={{ lines: 2 }}
              fontSize="sub"
              className="context-item-value-text"
            >
              {dropName}
            </TextTruncate>
          )}
        </div>
      </div>

      {(cutoffTime || order.amendmentExpiryDate) && (
        <div className="order-by context-item">
          <Text
            className="context-item-label"
            fontSize="sub"
          >
            Order by
          </Text>

          <div className="context-item-value">
            <Icon
              icon={Icons.ERRORCIRCLE}
              $fillColor="accentLight"
              size={15}
            />

            <Text className="context-item-value-text">
              <Datetime
                template="count_down"
                date={order.amendmentExpiryDate || cutoffTime}
              />
            </Text>
          </div>
        </div>
      )}

      {order.orderDeliveryDate && (
        <div className="delivery-date context-item">
          <Text
            className="context-item-label"
            fontSize="sub"
          >
            Delivery date
          </Text>

          <div className="context-item-value">
            <Icon
              icon={Icons.CALENDAR}
              $fillColor="white"
              size={15}
            />

            <Text className="context-item-value-text">
              <Datetime
                ignoreLocal
                template="long_date"
                date={order.orderDeliveryDate}
              />
            </Text>
          </div>
        </div>
      )}

      <div
        className="cart-outer"
        onClick={() => history.push(`/orders/${order.id}`)}
        data-cypress="cart-summary"
      >
        <div className="cart context-item">
          <Text
            className="context-item-label"
            fontSize="sub"
          >
            checkout
          </Text>

          <div className="context-item-value">
            <Icon
              icon={Icons.CART}
              $fillColor="white"
              size={15}
            />

            <Text
              className="context-item-value-text"
              color={theme.overrides?.menu?.textColor ? theme.overrides?.menu?.textColor : "white"}
            >
              {order.orderLines ?
                pricesVisible ? (
                  <>
                    <TotalPrice
                      lines={order.orderLines}
                      id={order.id}
                      totalOnly
                    />

                    {` (${calculateCartTotalQuantity(order.orderLines)} items)`}
                  </>
                ) : `${calculateCartTotalQuantity(order.orderLines)} items` :
                "0 items"}

            </Text>

          </div>
        </div>

        <Icon
          className="right-icon"
          icon={Icons.CHEVRONRIGHT}
          $fillColor="white"
          size={25}
        />
      </div>

    </InOrderWrapper>
  );
};

const InOrderWrapper = styled(Grid.Bounds)`
  ${({ theme }) => css`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex: 1;
    flex-basis: auto;
    height: 100%;
    flex-wrap: wrap;

    .cart-outer {
      display: flex; 
      flex-direction: row;
      background: ${theme.colors.primaryDark};
      align-items: center;

      .right-icon {
        transition: ${theme.transition.quickest};
        transform: translateX(-5px);
      }
      
      :hover {
        cursor: pointer; 
        background: ${lighten(0.02, theme.colors.primaryDark)};

        .right-icon {
          transform: translateX(0px);
        }
      }
    }

    .context-item {
      padding: 0 ${theme.spacing.padding.md};
      height: ${ContextHeight}px;
      align-items: flex-start;
      justify-content: center;
      display: flex;
      flex-direction: column;
      width: 50%;
      
      .context-item-label {
        color: ${theme.colors.accentLight};
        font-weight: ${theme.fonts.weights.bold};
        letter-spacing: 1px;
        text-transform: uppercase;
        display: block;
        padding-left: calc(15px + ${theme.spacing.margins.sm});
      }

      &.cart {
        background: ${theme.overrides?.contextMenu?.alternativeBackground ? theme.overrides.contextMenu.alternativeBackground : theme.colors.primaryDark};

        :hover {
          cursor: pointer; 
          background: ${theme.overrides?.contextMenu?.alternativeBackground ? lighten(0.02, theme.overrides.contextMenu.alternativeBackground) : lighten(0.02, theme.colors.primaryDark)};
        }
      }
    }

    .context-item-value {
      display: flex;
      align-items: center;
      
      svg {
        margin-right: ${theme.spacing.margins.sm};
        ${theme.overrides?.menu?.iconColor ? css`
          &:not(.warn){
            fill: ${theme.overrides.menu.iconColor} !important;
          }
        ` : ""}
      }
      
      .context-item-value-text, span {
        color: ${theme.overrides?.menu?.textColor ? theme.overrides?.menu?.textColor : theme.colors.white};
      }
    }

    ${breakpoint("md", theme)} {
      justify-content: flex-end;
      flex-wrap: none;

      .context-item {
        width: auto;
        max-width: 180px;

        &.drop-name-context-item {
          max-width: 200px;
        }

        &.cart {
          display: flex;
        }
      }
    }

  `}
`;
